import { fieldWithoutMarginStyles } from '@Guidelines/Theme/commonPassthrough';
import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import buildTypeSeriesMap from '@UI/Pages/Home/DocumentsBlock/DocumentTypeFilter/Helpers/buildTypeSeriesMap';
import homeDocumentTypeSignal, { setHomeDocumentType } from '@UI/Pages/Home/Signals/homeDocumentTypeSignal';
import type DocumentTypesListView from '@UI/Store/DocumentTypes/Views/DocumentTypesListView';
import type DocumentTypeCountView from '@UI/Store/Indicators/Views/DocumentTypeCountView';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { Dropdown } from '@verclam/ui-kit';

/** Above this many types, the panel gets a search field. */
const SEARCH_THRESHOLD = 7;
const ALL_TYPES_VALUE = 0;

type Option = {
	readonly id: number;
	readonly label: string;
	readonly isArchived: boolean;
};

const optionStyle = css`
	display: flex;
	align-items: center;
	gap: 0.5625rem;
	min-width: 0;
`;

const dotStyle = (color: string | null) => css`
	flex: none;
	box-sizing: border-box;
	width: 10px;
	height: 10px;
	border-radius: 50%;
	background-color: ${color ?? 'transparent'};
	border: ${color === null ? `1.5px solid ${theme.home.typeSeriesOther.fill}` : 'none'};
`;

const labelStyle = (isArchived: boolean) => css`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	color: ${isArchived ? theme.colors.inkFaint : 'inherit'};
`;

const passThrough = {
	...fieldWithoutMarginStyles,
	controlWrapper: {
		style: {
			height: 'auto',
			minHeight: 0,
			padding: '0.625rem 0.9375rem',
			borderRadius: theme.radii.pill,
			border: `1.5px solid ${theme.colors.borderStrong}`,
			backgroundColor: theme.colors.surfaceMuted,
			fontFamily: theme.fonts.body,
			fontSize: '0.8125rem',
			fontWeight: theme.fontWeights.medium,
		},
	},
	panel: {
		style: { borderRadius: theme.radii.medium, border: `1.5px solid ${theme.colors.borderStrong}` },
	},
	item: { style: { borderRadius: '12px' } },
} as const;

type Props = {
	readonly documentTypes: DocumentTypesListView;
	/** Breakdown shown beside the filter: a type takes the colour of its bar. */
	readonly byType: readonly DocumentTypeCountView[];
};

/** "Tous les types", then active types A→Z, then archived ones suffixed. Writes `homeDocumentTypeSignal`. */
const DocumentTypeFilter: React.FC<Props> = ({ documentTypes, byType }) => {
	const selectedTypeId = useSignalValue(homeDocumentTypeSignal);
	const seriesByType = React.useMemo(
		() => buildTypeSeriesMap(byType, documentTypes),
		[byType, documentTypes],
	);

	const options = React.useMemo<readonly Option[]>(() => {
		const byLabel = (left: Option, right: Option): number => left.label.localeCompare(right.label, 'fr');
		const active = documentTypes
			.activeTypes()
			.map((documentType) => ({ id: documentType.id, label: documentType.label, isArchived: false }))
			.sort(byLabel);
		const archived = documentTypes
			.archivedTypes()
			.map((documentType) => ({
				id: documentType.id,
				label: `${documentType.label} · archivé`,
				isArchived: true,
			}))
			.sort(byLabel);

		return [{ id: ALL_TYPES_VALUE, label: 'Tous les types', isArchived: false }, ...active, ...archived];
	}, [documentTypes]);

	const handleChange = React.useCallback((value: number | null) => {
		setHomeDocumentType(value === null || value === ALL_TYPES_VALUE ? null : value);
	}, []);

	const renderOption = React.useCallback(
		(option: unknown): React.ReactNode => {
			if (option === null || option === undefined) {
				return null;
			}

			const { id, label, isArchived } = option as Option;

			return (
				<span css={optionStyle}>
					<span css={dotStyle(seriesByType.get(id)?.fill ?? null)} aria-hidden="true" />
					<span css={labelStyle(isArchived)}>{label}</span>
				</span>
			);
		},
		[seriesByType],
	);

	return (
		<Dropdown<number>
			name="homeDocumentType"
			label="Type de document"
			isLabelHidden
			options={options}
			optionLabel="label"
			optionValue="id"
			value={selectedTypeId ?? ALL_TYPES_VALUE}
			onChange={handleChange}
			filter={options.length - 1 > SEARCH_THRESHOLD}
			size="sm"
			itemTemplate={renderOption}
			valueTemplate={renderOption}
			pt={passThrough}
		/>
	);
};

export default DocumentTypeFilter;
